import { useEffect, useId, useRef } from "react";
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  MenuItem,
  Stack,
  TextField,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import "dayjs/locale/de";
import { useTranslation } from "react-i18next";
import { parameterControlsSx } from "@/components/documentTemplate/parameterUiStyles";
import type {
  DocumentTemplateParameter,
  ParameterValue,
  ParameterValues,
} from "@/types/DocumentTemplateParameter";

interface Props {
  definitions: DocumentTemplateParameter[];
  values: ParameterValues;
  errors?: Record<string, string>;
  onChange: (name: string, value: ParameterValue) => void;
}

export default function TemplateParameterFields({
  definitions,
  values,
  errors = {},
  onChange,
}: Props) {
  const { t } = useTranslation();
  const id = useId();
  const container = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (Object.keys(errors).length)
      container.current
        ?.querySelector<HTMLElement>('[aria-invalid="true"]')
        ?.focus();
  }, [errors]);

  return (
    <Stack ref={container} spacing={2} sx={parameterControlsSx}>
      {definitions.map((definition) => {
        const value = values[definition.name] ?? null;
        const error = errors[definition.name];
        const helperText = error
          ? t(error, { count: definition.max_length ?? 10000 })
          : definition.help_text;
        const common = {
          id: `${id}-${definition.name}`,
          label: definition.label,
          required: !!definition.required,
          error: !!error,
          helperText,
          fullWidth: true,
          size: "small" as const,
        };
        if (definition.type === "boolean")
          return (
            <FormControl key={definition.name} error={!!error}>
              <FormControlLabel
                label={definition.label}
                control={
                  <Checkbox
                    checked={value === true}
                    inputProps={{ "aria-invalid": !!error }}
                    onChange={(_, checked) =>
                      onChange(definition.name, checked)
                    }
                  />
                }
              />
              {helperText && <FormHelperText>{helperText}</FormHelperText>}
            </FormControl>
          );
        if (definition.type === "select")
          return (
            <TextField
              key={definition.name}
              {...common}
              select
              value={
                value === null
                  ? ""
                  : String(
                      definition.choices?.findIndex(
                        (choice) => choice.value === value
                      ) ?? -1
                    )
              }
              onChange={(event) =>
                onChange(
                  definition.name,
                  event.target.value === ""
                    ? null
                    : definition.choices![Number(event.target.value)].value
                )
              }
            >
              <MenuItem value="">—</MenuItem>
              {definition.choices?.map((choice, index) => (
                <MenuItem key={index} value={String(index)}>
                  {choice.label}
                </MenuItem>
              ))}
            </TextField>
          );
        if (definition.type === "date")
          return (
            <LocalizationProvider
              key={definition.name}
              dateAdapter={AdapterDayjs}
              adapterLocale="de"
            >
              <DatePicker
                label={definition.label}
                format="DD.MM.YYYY"
                value={value ? dayjs(String(value)) : null}
                onChange={(date) =>
                  onChange(
                    definition.name,
                    date === null
                      ? null
                      : date.isValid()
                      ? date.format("YYYY-MM-DD")
                      : "invalid"
                  )
                }
                slotProps={{ textField: common }}
              />
            </LocalizationProvider>
          );
        return (
          <TextField
            key={definition.name}
            {...common}
            value={value ?? ""}
            inputProps={{
              inputMode: ["integer", "decimal"].includes(definition.type)
                ? "decimal"
                : "text",
            }}
            onChange={(event) => onChange(definition.name, event.target.value)}
          />
        );
      })}
    </Stack>
  );
}
